import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ActivityAction, ActivityLogEntry } from '../types';
import activityService from '../services/activityService';
import {
  PageHeader, Badge, Button, Field, Input, DateInput, Select, Icon,
  LoadingScreen, EmptyState, tableCard, tableScroll, th, td, tableRow,
  Spinner,
  filterAktiv,
} from '../components/ui';

const PAGE_SIZE = 50;

// Pfad-Wortstamm (entityType) → sprechendes Label.
const ENTITY_LABELS: Record<string, string> = {
  kunden: 'Kunde',
  graeber: 'Grab',
  tiere: 'Tier',
  vertraege: 'Vertrag',
  friedhoefe: 'Friedhof',
  users: 'Benutzer',
  dokumente: 'Dokument',
  // Altbestand: das Feature ist entfallen, historische Protokollzeilen bleiben aber lesbar.
  erinnerungen: 'Erinnerung',
  artikel: 'Artikel',
  perioden: 'Periode',
  vertragsartikel: 'Vertragsposition',
};

const ACTION_LABELS: Record<ActivityAction, string> = {
  LOGIN: 'Anmeldung',
  LOGIN_FAILED: 'Anmeldung fehlgeschlagen',
  LOGOUT: 'Abmeldung',
  CREATE: 'Angelegt',
  UPDATE: 'Bearbeitet',
  DELETE: 'Gelöscht',
};

const ACTION_BADGE: Record<ActivityAction, string> = {
  LOGIN: 'bg-emerald-100 text-emerald-800',
  LOGIN_FAILED: 'bg-red-100 text-red-800',
  LOGOUT: 'bg-gray-100 text-gray-700',
  CREATE: 'bg-blue-100 text-blue-800',
  UPDATE: 'bg-amber-100 text-amber-800',
  DELETE: 'bg-red-100 text-red-800',
};

const formatTimestamp = (ts?: string): string => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const objektLabel = (e: ActivityLogEntry): string => {
  if (!e.entityType) return '—';
  const label = ENTITY_LABELS[e.entityType] ?? e.entityType;
  return e.entityId ? `${label} #${e.entityId}` : label;
};

const Aktivitaet: React.FC = () => {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [laedtMehr, setLaedtMehr] = useState(false);

  // Filter
  const [action, setAction] = useState('');
  const [username, setUsername] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Wie die übrigen Listen ohne Blättern: der Server liefert seitenweise, beim Scrollen ans Ende wird
  // die nächste Seite geholt und angehängt. Nachgeladen wird mit dem Filter, der zuletzt *angewendet*
  // wurde (nicht mit halb ausgefüllten Feldern); eine ältere Antwort nach einem Filterwechsel verfällt.
  type Filter = { action: string; username: string; from: string; to: string };
  const aktiverFilter = useRef<Filter>({ action: '', username: '', from: '', to: '' });
  const anfrage = useRef(0);

  // `filter` überschreibt die Filter aus dem State – beim Zurücksetzen sind die neuen Werte im
  // State noch nicht angekommen, `load` sähe sonst die alten.
  const load = useCallback(async (
    targetPage: number,
    filter: Filter = { action, username, from, to },
    anhaengen = false,
  ) => {
    const nr = ++anfrage.current;
    if (anhaengen) setLaedtMehr(true); else { setLoading(true); aktiverFilter.current = filter; }
    try {
      const result = await activityService.getActivity({
        action: filter.action || undefined,
        username: filter.username || undefined,
        from: filter.from || undefined,
        to: filter.to || undefined,
        page: targetPage,
        size: PAGE_SIZE,
      });
      if (nr !== anfrage.current) return;
      setEntries(vorher => (anhaengen ? [...vorher, ...result.items] : result.items));
      setTotal(result.total);
      setPage(result.page);
    } catch (error) {
      toast.error('Aktivität konnte nicht geladen werden');
      console.error('Failed to load activity:', error);
    } finally {
      if (nr === anfrage.current) { setLoading(false); setLaedtMehr(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, username, from, to]);

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    load(0);
  };

  const resetFilters = () => {
    setAction('');
    setUsername('');
    setFrom('');
    setTo('');
    // Explizit ohne Filter laden – die State-Updates oben greifen erst beim nächsten Render.
    load(0, { action: '', username: '', from: '', to: '' });
  };

  // Hervorgehoben wird, was tatsächlich angewendet ist – nicht schon das, was gerade getippt wird.
  const angewendet = aktiverFilter.current;
  const filterAn = !!(angewendet.action || angewendet.username || angewendet.from || angewendet.to);

  // Unsichtbarer Anker am Listenende: kommt er in Sicht, wird die nächste Seite angehängt.
  const anker = useRef<HTMLDivElement | null>(null);
  const hatMehr = entries.length < total;
  useEffect(() => {
    const el = anker.current;
    if (!el || !hatMehr || loading || laedtMehr) return;
    const beobachter = new IntersectionObserver(e => {
      if (e[0].isIntersecting) load(page + 1, aktiverFilter.current, true);
    }, { rootMargin: '600px' });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [hatMehr, loading, laedtMehr, page, load]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Aktivität"
        subtitle="Anmeldungen und Änderungen an Datensätzen"
        chip={<Badge className="bg-brand-100 text-brand-800">{total} Einträge</Badge>}
      />

      {/* Filterleiste */}
      <form onSubmit={applyFilters} className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
        <Field label="Aktion">
          <Select value={action} onChange={(e) => setAction(e.target.value)} className={angewendet.action ? filterAktiv : ''}>
            <option value="">Alle</option>
            <option value="LOGIN">Anmeldung</option>
            <option value="LOGIN_FAILED">Anmeldung fehlgeschlagen</option>
            <option value="LOGOUT">Abmeldung</option>
            <option value="CREATE">Angelegt</option>
            <option value="UPDATE">Bearbeitet</option>
            <option value="DELETE">Gelöscht</option>
          </Select>
        </Field>
        <Field label="Benutzer">
          <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Benutzername" className={angewendet.username ? filterAktiv : ''} />
        </Field>
        <Field label="Von">
          <DateInput value={from} onChange={setFrom} className={angewendet.from ? filterAktiv : ''} />
        </Field>
        <Field label="Bis">
          <DateInput value={to} onChange={setTo} className={angewendet.to ? filterAktiv : ''} />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" icon={<Icon.Search className="h-4 w-4" />}>Filtern</Button>
        </div>
      </form>

      {/* Wie in den anderen Listen: mit Filter deutlich, wie viele passen, und ein klarer Weg zurück. */}
      {filterAn && !loading && (
        <div className="mb-3 flex items-center justify-end gap-3 text-sm text-gray-500">
          <span className="tabular-nums"><span className="font-semibold text-gray-900">{total}</span> passende Einträge</span>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex min-h-[36px] items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-50 px-3 text-sm font-medium text-brand-800 ring-1 ring-brand-200 transition-colors hover:bg-brand-100"
          >
            <Icon.Close className="h-3.5 w-3.5" />Filter zurücksetzen
          </button>
        </div>
      )}

      {loading ? (
        <LoadingScreen />
      ) : (
        <div className={tableCard}>
          <div className={tableScroll}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={th}>Zeitpunkt</th>
                  <th className={th}>Benutzer</th>
                  <th className={th}>Aktion</th>
                  <th className={th}>Objekt</th>
                  <th className={th}>Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((e) => (
                  <tr key={e.id} className={tableRow}>
                    <td className={`${td} whitespace-nowrap text-gray-600`}>{formatTimestamp(e.timestamp)}</td>
                    <td className={`${td} font-medium text-gray-900`}>{e.username ?? '—'}</td>
                    <td className={td}>
                      <Badge className={ACTION_BADGE[e.action]}>{ACTION_LABELS[e.action]}</Badge>
                    </td>
                    <td className={`${td} text-gray-700`}>{objektLabel(e)}</td>
                    <td className={`${td} text-gray-500`}>
                      {e.details || e.ipAddress || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {entries.length === 0 && (
            action || username || from || to ? (
              <EmptyState
                title="Keine Treffer für diesen Filter"
                hint="Erweitern Sie den Zeitraum oder wählen Sie eine andere Aktion."
                action={<Button variant="secondary" onClick={resetFilters}>Filter zurücksetzen</Button>}
              />
            ) : (
              <EmptyState
                title="Noch keine Aktivität"
                hint="Hier erscheinen Anmeldungen und Änderungen an Datensätzen."
              />
            )
          )}
        </div>
      )}

      {/* Nachladen beim Scrollen – siehe `anker` oben. */}
      {!loading && hatMehr && (
        <div ref={anker} className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
          {laedtMehr && <><Spinner className="h-4 w-4" /> Ältere Einträge werden geladen</>}
        </div>
      )}
    </div>
  );
};

export default Aktivitaet;
